import React, { useState } from 'react';
import { Modal, Text, Heading } from './ui';
import { acceptFriendRequest, rejectFriendRequest } from '../services/api';
import { useAuth } from '../context/authContext';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

const FriendRequestModal = ({ isOpen, onClose, notification, onRequestHandled }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (!notification) {
        return null;
    }

    const senderName = notification.metadata?.sender_name || 'Someone';
    const message = notification.metadata?.message;
    const requestId = notification.metadata?.request_id;

    const handleAccept = async () => {
        if (!user || !requestId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const token = await user.getIdToken();
            await acceptFriendRequest(requestId, token);
            
            // Notify parent that request was handled
            if (onRequestHandled) {
                await onRequestHandled('accepted');
            }
        } catch (err) {
            console.error('Error accepting friend request:', err);
            setError(err.message || 'Failed to accept friend request');
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        if (!user || !requestId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const token = await user.getIdToken();
            await rejectFriendRequest(requestId, token);
            
            // Notify parent that request was handled
            if (onRequestHandled) {
                await onRequestHandled('rejected');
            }
        } catch (err) {
            console.error('Error rejecting friend request:', err);
            setError(err.message || 'Failed to reject friend request');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        // Notify parent that request was canceled
        if (onRequestHandled) {
            onRequestHandled('canceled');
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Friend Request" maxWidth="max-w-2xl">
            <div className="space-y-4">
                {/* User Profile Section */}
                <div className="flex items-start space-x-3">
                    <div className="w-12 h-12 bg-neutral-0 hairline flex items-center justify-center text-lg font-mono flex-shrink-0">
                        {senderName.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                        <Heading level={4} className="text-neutral-900 mb-1">
                            {senderName}
                        </Heading>
                        <Text className="text-sm text-neutral-900 opacity-80 mb-3">
                            wants to be friends with you
                        </Text>
                        
                        {message && (
                            <div className="bg-neutral-50 p-3 hairline">
                                <Text className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 mb-2">
                                    MESSAGE
                                </Text>
                                <Text className="text-sm text-neutral-900 italic">
                                    "{message}"
                                </Text>
                            </div>
                        )}
                    </div>
                </div>
                
                <div className="h-px bg-neutral-900 opacity-20"></div>
                
                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 p-3 hairline">
                        <Text className="text-red-600 text-sm">{error}</Text>
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex space-x-3">
                    <button
                        onClick={handleAccept}
                        disabled={loading}
                        className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity disabled:opacity-40"
                    >
                        {loading ? 'PROCESSING...' : 'ACCEPT'}
                    </button>
                    
                    <button
                        onClick={handleReject}
                        disabled={loading}
                        className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider disabled:opacity-40"
                    >
                        {loading ? 'PROCESSING...' : 'REJECT'}
                    </button>
                    
                    <button
                        onClick={handleCancel}
                        disabled={loading}
                        className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider disabled:opacity-40"
                    >
                        CANCEL
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default FriendRequestModal;

